// src/components/layout/RequireOnboarding.jsx
// Bouncer: redirects to /onboarding if user has not completed onboarding or has no study plan.
// Wrap all post-signup routes (dashboard, exam, practice, study-plan, etc.) so they all require onboarding.
import React, { useEffect, useState } from 'react';
import { Outlet, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import enhancedUserService from '../../services/enhancedUserService';
import { isSupportedLang } from '../../i18n';

const DEFAULT_LANG = 'en';

export default function RequireOnboarding({ user, personalizedPlan }) {
  const { t } = useTranslation();
  const { lang } = useParams();
  const currentLang = isSupportedLang(lang) ? lang : DEFAULT_LANG;
  const [checking, setChecking] = useState(true);
  const [onboardingComplete, setOnboardingComplete] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setChecking(false);
      setOnboardingComplete(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const data = await enhancedUserService.getOnboardingComplete(user.uid);
      if (cancelled) return;
      setOnboardingComplete(!!(data && (data.examName != null || data.placementLevel != null)));
      setChecking(false);
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  if (!user?.uid) return <Outlet />;

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-black/60">{t('onboarding.loading', 'Loading...')}</p>
      </div>
    );
  }

  if (!onboardingComplete) {
    return <Navigate to={`/${currentLang}/onboarding`} replace />;
  }

  return <Outlet />;
}
